import moment from "moment";
import Image from "next/image";
import { FacebookShareButton } from "react-share";
import ExtraPageFooter from "../../components/Common/ExtraPageFooter";
import PageHeadingComponent from "../../components/Common/PageHeadingComponent";
import SectionsRender from "../../components/Common/SectionsRender";
import Footer from "../../components/_App/Footer";
import Header from "../../components/_App/Header";
import { ARTICLE_LIST_QUERY, ARTICLE_QUERY } from "../../data/pageQuery";
import cacheClient from "../../data/cacheClient";
import queryWithRetry from "../../data/queryWithRetry";
import { PHASE_PRODUCTION_BUILD } from "next/constants";

const ArticleDetails = ({ data, id, seo, menu, footer, created }) => {
  const isAU = process.env.NEXT_PUBLIC_SITE_IS_AU == "true";
  const baseUrl = isAU ? "https://rycofilters.com.au" : "https://ryco.co.nz";
  const dd = moment(created).format("Do MMM YY");
  const url = `${baseUrl}/articles/${id}`;
  const coverImage = data?.coverImage
    ? data?.coverImage?.length > 0
      ? data?.coverImage[0]?.url + "?format=png"
      : null
    : null;

  return (
    <>
      <Header {...menu} />

      <div className='page-wrapper flex-grow-1'>
        <div itemScope itemType='https://schema.org/NewsArticle'>
          {coverImage && <meta itemProp='image' content={coverImage} />}
          <PageHeadingComponent {...data} />
          <div className='container pb-5'>
            <hr />
            <div itemProp='datePublished' content={created}>
              RYCO FILTERS - {dd}
            </div>
            <div>
              {data.keywords &&
                data.keywords.map((x, idx1) => {
                  return (
                    <span
                      key={idx1}
                      className='badge rounded-pill bg-secondary'
                    >
                      #{x}
                    </span>
                  );
                })}
              <hr />
            </div>
          </div>
          <SectionsRender {...data} />
          <div className='py-3'>
            <div className='container'>
              <p>
                Share this via: &nbsp;
                <FacebookShareButton url={url} quote={data.heading}>
                  <Image
                    src="/icons/facebook-black.svg"
                    width='32'
                    height='32'
                    alt='Facebook'
                    layout='raw'
                  />
                </FacebookShareButton>
              </p>
            </div>
          </div>
        </div>
      </div>
      <ExtraPageFooter />
      <Footer {...footer} />
    </>
  );
};

export async function getStaticProps({ params }) {
  const { id } = params;
  const filter = `data/pageId/iv eq '${id}'`;
  var pageDataList, footerContents, menuContents;  

  const loadArticleFromCms = async () => {
    const { data } = await queryWithRetry({
      query: ARTICLE_QUERY,
      variables: { filter },
      fetchPolicy: "no-cache",
    });
    return data;
  };

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD ) {    
    //check in local cache
    let cache = cacheClient();
    let cachePageData = cache.get("articles");
    if (Array.isArray(cachePageData)) {
      footerContents = cache.get("footerContents");
      menuContents = cache.get("menuContents");
      let filterPageData = cachePageData.find(x => x.flatData?.pageId == id);
      pageDataList = [filterPageData];
    } else {
      const data = await loadArticleFromCms();
      pageDataList = data.queryArticleContents;
      footerContents = data.queryFooterContents;
      menuContents = data.queryMenuContents;
    }
  } else {        
    const data = await loadArticleFromCms();
    pageDataList  = data.queryArticleContents;
    footerContents = data.queryFooterContents;
    menuContents = data.queryMenuContents;    
  }

  const notFound = pageDataList.length == 0;
  const pageData = pageDataList.length > 0 ? pageDataList[0].flatData : {};
  const footerContent =
    footerContents[0] != null ? footerContents[0].flatData : [];
  const menuContent = menuContents[0] != null ? menuContents[0].flatData : [];
  const created = pageDataList.length > 0 ? pageDataList[0].created : "";
  const coverImage = pageData?.coverImage
    ? pageData?.coverImage?.length > 0
      ? pageData?.coverImage[0]?.url + "?format=png"
      : null
    : null;
  const url = `/articles/${params.id}`;
  const isNzOnly = !pageData.isAU && pageData.isNZ;

  return {
    props: {
      id: params.id,
      menu: menuContent,
      footer: footerContent,
      seo: {
        title: pageData.heading + " | Ryco Filters Article",
        metadata: `${pageData.abstract ?? "Ryco Filters Article"}`,
        keywords: pageData.keywords ? pageData.keywords : null,
        pic: coverImage,
        socialTitle: `${pageData.heading}`,
        socialIsArticle: false,
        isNzOnly: false,
        url: url,
        isNzOnly: isNzOnly,
      },
      data: pageData,
      created: created,
    },
    notFound: notFound,
    revalidate: 60 * 1,
  };
}

export async function getStaticPaths() {
  const isAU = process.env.NEXT_PUBLIC_SITE_IS_AU == "true";
  const country = isAU ? "isAU" : "isNZ";
  const filter = `data/${country}/iv eq true`;

  let pageList = [];
  try {
    const { data } = await queryWithRetry({
      query: ARTICLE_LIST_QUERY,
      variables: { filter },
    });
    pageList = data.queryArticleContents ?? [];
  } catch (error) {
    console.log(
      "Failed to load article paths. Article pages will be generated on request.",
      error
    );
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  const mappedIndex = pageList.map(({ id, flatData }) => ({
    params: {
      id: flatData.pageId,
      name: flatData.pageId,
      title: flatData.heading,
      meta: flatData.abstract,
      cmsId: id,
    },
  }));

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD ) {    
    console.log("Loading all articles data");
    const cache = cacheClient();
    cache.clear("articles");
    try {
      const dataAll = await queryWithRetry({
        query: ARTICLE_QUERY,
        variables: { filter: filter },
        fetchPolicy: "network-only",
      });
      const {
        queryArticleContents: pageDataList      
      } = dataAll.data;
      console.log("Save all articles locally during production build");
      cache.set("articles", pageDataList);
    } catch (error) {
      console.log(
        "Failed to preload articles. Each article will be loaded individually during the build.",
        error
      );
    }
  }  


  return {
    paths: mappedIndex,
    fallback: "blocking",
  };
}

export default ArticleDetails;
